import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Select, 
  Button, 
  Alert, 
  Table, 
  Typography, 
  Space,
  Input,
  Row,
  Col,
  Tag,
  Divider,
  Spin,
  Tooltip,
  Progress,
  Modal,
  message,
  DatePicker,
  Form,
  Radio,
  Switch,
  Slider,
  Steps,
  Popconfirm
} from 'antd';
import { 
  FilterOutlined,
  UserOutlined,
  EyeOutlined,
  SendOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
  ReloadOutlined,
  PhoneOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  DollarOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  HistoryOutlined,
  TemplateOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { useAuth } from '../../contexts/AuthContext';
import {
  getAvailableCountries,
  getAvailableStates,
  getAvailableSites,
  getAvailableRoles,
  getFilteredStaff,
  logFilterUsage,
  validateFilters,
  generateFilteringSummary
} from '../../services/bulkSMSService';
import {
  createBulkSMSCampaign,
  queueBulkSMSCampaign,
  validateBulkSMSCampaign,
  calculateSMSCost,
  processMessageTemplate,
  getRecentCampaigns,
  PRIORITY_LEVELS
} from '../../services/enterpriseBulkSMSService';
import { getOrgSmsTemplates, extractVariables } from '../../services/orgSmsTemplateService';
import BulkSMSAnalyticsDashboard from './BulkSMSAnalyticsDashboard';
import { db } from '../../config/firebase';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Step } = Steps;

/**
 * BulkSMSPanel - Phase 2: Enterprise Bulk SMS System
 * 
 * Comprehensive SMS system with:
 * - Advanced filtering and targeting
 * - Template integration with variables
 * - Message composition and scheduling
 * - Real-time delivery tracking
 * - Campaign analytics and management
 * - Compliance and cost optimization
 * 
 * @param {string} orgId - Organization ID to filter staff for
 * @param {string} currentUserRole - Current user role (super_admin, org_admin, etc.)
 * @param {boolean} isReadOnly - Phase 2: false (full functionality enabled)
 * @param {Object} crisisContext - Optional crisis context for targeted messaging
 * @param {function} onStaffFiltered - Callback when staff filtering is complete
 */
const BulkSMSPanel = ({ 
  orgId, 
  currentUserRole = 'org_admin',
  isReadOnly = false, // Phase 2: Full functionality enabled
  crisisContext = null,
  onStaffFiltered = null
}) => {
  const { currentUser } = useAuth();
  const [form] = Form.useForm();
  
  // Workflow steps
  const [currentStep, setCurrentStep] = useState(0);
  const [campaignData, setCampaignData] = useState({});
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Filter options data
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [sites, setSites] = useState([]);
  const [roles, setRoles] = useState([]);
  
  // Filter selections
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  
  // Results
  const [filteredResults, setFilteredResults] = useState(null);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [summaryModalVisible, setSummaryModalVisible] = useState(false);
  const [filteringSummary, setFilteringSummary] = useState(null);
  
  // Message composition
  const [messageText, setMessageText] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateVariables, setTemplateVariables] = useState({});
  const [messagePreview, setMessagePreview] = useState('');
  
  // Scheduling and priority
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [priority, setPriority] = useState(PRIORITY_LEVELS.NORMAL);
  
  // Campaign management
  const [recentCampaigns, setRecentCampaigns] = useState([]);
  const [activeCampaignId, setActiveCampaignId] = useState(null);
  const [analyticsVisible, setAnalyticsVisible] = useState(false);
  
  // Cost calculation
  const [costEstimate, setCostEstimate] = useState(null);
  
  // Initialize component
  useEffect(() => {
    if (orgId) {
      initializeComponent();
    }
  }, [orgId]);
  
  // Update cost estimate when message or results change
  useEffect(() => {
    if (messageText && filteredResults?.totalCount > 0) {
      const estimate = calculateSMSCost(messageText, filteredResults.totalCount);
      setCostEstimate(estimate);
    } else {
      setCostEstimate(null);
    }
  }, [messageText, filteredResults]);
  
  // Update message preview when template or variables change
  useEffect(() => {
    if (selectedTemplate && messageText) {
      const preview = processMessageTemplate(messageText, {
        ...templateVariables,
        organization_name: 'Your Organization', // Preview placeholder
        site_name: 'Sample Site',
        first_name: 'John',
        last_name: 'Doe'
      });
      setMessagePreview(preview);
    } else {
      setMessagePreview(messageText);
    }
  }, [messageText, selectedTemplate, templateVariables]);
  
  /**
   * Initialize all component data
   */
  const initializeComponent = async () => {
    setInitializing(true);
    try {
      await Promise.all([
        initializeFilters(),
        loadTemplates(),
        loadRecentCampaigns()
      ]);
    } catch (error) {
      console.error('Error initializing bulk SMS panel:', error);
    } finally {
      setInitializing(false);
    }
  };
  
  /**
   * Initialize filter options
   */
  const initializeFilters = async () => {
    setInitializing(true);
    try {
      console.log('🔄 Initializing bulk SMS filters for org:', orgId);
      
      const [countriesData, rolesData] = await Promise.all([
        getAvailableCountries(orgId),
        getAvailableRoles(orgId)
      ]);
      
      setCountries(countriesData);
      setRoles(rolesData);
      
      console.log('✅ Filters initialized:', { 
        countries: countriesData.length, 
        roles: rolesData.length 
      });
      
    } catch (error) {
      console.error('❌ Error initializing filters:', error);
      message.error('Failed to load filtering options');
    } finally {
      setInitializing(false);
    }
  };
  
  /**
   * Handle country selection change
   */
  const handleCountryChange = async (country) => {
    setSelectedCountry(country);
    setSelectedState('');
    setSelectedSite('');
    setStates([]);
    setSites([]);
    
    if (country) {
      setLoading(true);
      try {
        const statesData = await getAvailableStates(orgId, country);
        setStates(statesData);
      } catch (error) {
        console.error('❌ Error loading states:', error);
        message.error('Failed to load states');
      } finally {
        setLoading(false);
      }
    }
  };
  
  /**
   * Handle state selection change
   */
  const handleStateChange = async (state) => {
    setSelectedState(state);
    setSelectedSite('');
    setSites([]);
    
    if (state && selectedCountry) {
      setLoading(true);
      try {
        const sitesData = await getAvailableSites(orgId, selectedCountry, state);
        setSites(sitesData);
      } catch (error) {
        console.error('❌ Error loading sites:', error);
        message.error('Failed to load sites');
      } finally {
        setLoading(false);
      }
    }
  };
  
  /**
   * Apply filters and get matching staff
   */
  const applyFilters = async () => {
    setLoading(true);
    try {
      const filters = {
        country: selectedCountry,
        state: selectedState,
        siteName: selectedSite,
        role: selectedRole
      };
      
      // Remove empty filters
      Object.keys(filters).forEach(key => {
        if (!filters[key]) delete filters[key];
      });
      
      console.log('🎯 Applying filters:', filters);
      
      // Validate filters
      const validation = validateFilters(filters);
      if (!validation.isValid) {
        message.error('Invalid filter combination: ' + validation.errors.join(', '));
        return;
      }
      
      // Show warnings if any
      validation.warnings.forEach(warning => {
        message.warning(warning);
      });
      
      // Get filtered staff
      const results = await getFilteredStaff(orgId, filters);
      setFilteredResults(results);
      
      // Log filter usage
      await logFilterUsage(orgId, filters, results.totalCount, currentUser?.uid);
      
      // Callback for parent component
      if (onStaffFiltered) {
        onStaffFiltered(results);
      }
      
      console.log('✅ Filters applied successfully:', results.totalCount, 'staff found');
      
    } catch (error) {
      console.error('❌ Error applying filters:', error);
      message.error('Failed to filter staff');
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Clear all filters
   */
  const clearFilters = () => {
    setSelectedCountry('');
    setSelectedState('');
    setSelectedSite('');
    setSelectedRole('');
    setStates([]);
    setSites([]);
    setFilteredResults(null);
    setMessageText('');
    setSelectedTemplate(null);
    setTemplateVariables({});
    setCostEstimate(null);
    setCurrentStep(0);
    form.resetFields();
  };

  /**
   * Load SMS templates for the organization
   */
  const loadTemplates = async () => {
    try {
      const templatesData = await getOrgSmsTemplates(db, orgId);
      const allTemplates = [
        ...templatesData.emergency.map(t => ({ ...t, category: 'emergency' })),
        ...templatesData.operational.map(t => ({ ...t, category: 'operational' })),
        ...templatesData.general.map(t => ({ ...t, category: 'general' }))
      ];
      setTemplates(allTemplates);
      console.log(`📱 SMS Panel: Loaded ${allTemplates.length} templates`);
    } catch (error) {
      console.error('❌ Error loading templates:', error);
    }
  };

  /**
   * Load recent campaigns for quick access
   */
  const loadRecentCampaigns = async () => {
    try {
      const campaigns = await getRecentCampaigns(orgId, 5);
      setRecentCampaigns(campaigns);
      console.log(`📱 SMS Panel: Loaded ${campaigns.length} recent campaigns`);
    } catch (error) {
      console.error('❌ Error loading recent campaigns:', error);
    }
  };

  /**
   * Handle template selection
   */
  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      setMessageText(template.message);
      
      // Initialize template variables
      const variables = extractVariables(template.message);
      const initialVariables = {};
      variables.forEach(variable => {
        initialVariables[variable] = '';
      });
      setTemplateVariables(initialVariables);
    }
  };

  /**
   * Handle template variable change
   */
  const handleVariableChange = (variable, value) => {
    setTemplateVariables(prev => ({
      ...prev,
      [variable]: value
    }));
  };

  /**
   * Move to next step in workflow
   */
  const nextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, 3));
  };

  /**
   * Move to previous step in workflow
   */
  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  /**
   * Validate current step before proceeding
   */
  const validateCurrentStep = () => {
    switch (currentStep) {
      case 0: // Filtering
        return filteredResults && filteredResults.totalCount > 0;
      case 1: // Message composition
        return messageText.trim().length > 0;
      case 2: // Review and send
        return true;
      default:
        return false;
    }
  };

  /**
   * Create and send SMS campaign
   */
  const sendSMSCampaign = async () => {
    try {
      setSending(true);

      // Validate campaign data
      const campaignData = {
        name: `SMS Campaign ${new Date().toLocaleDateString()}`,
        message: messageText,
        orgId: orgId,
        filters: {
          country: selectedCountry,
          state: selectedState,
          siteName: selectedSite,
          role: selectedRole
        },
        templateId: selectedTemplate?.id,
        templateVariables: templateVariables,
        priority: priority,
        scheduledFor: isScheduled ? scheduledDate?.toDate() : null,
        createdBy: currentUser?.uid,
        crisisContext: crisisContext
      };

      // Remove empty filters
      Object.keys(campaignData.filters).forEach(key => {
        if (!campaignData.filters[key]) delete campaignData.filters[key];
      });

      // Validate campaign
      const validation = await validateBulkSMSCampaign(campaignData);
      if (!validation.isValid) {
        message.error('Campaign validation failed: ' + validation.errors.join(', '));
        return;
      }

      // Show warnings
      validation.warnings.forEach(warning => {
        message.warning(warning);
      });

      // Create campaign
      const campaignId = await createBulkSMSCampaign(campaignData);
      setActiveCampaignId(campaignId);

      // Queue messages for sending
      const queueResult = await queueBulkSMSCampaign(
        campaignId,
        campaignData.filters,
        messageText,
        {
          orgId: orgId,
          organizationName: 'Crisis Compass', // TODO: Get actual org name
          scheduledFor: campaignData.scheduledFor,
          priority: priority,
          incidentId: crisisContext?.incidentId,
          userInitiated: currentUser?.uid
        }
      );

      message.success(`Campaign queued successfully! ${queueResult.queuedCount} messages will be sent.`);
      
      // Show analytics
      setAnalyticsVisible(true);
      
      // Reload recent campaigns
      await loadRecentCampaigns();

    } catch (error) {
      console.error('❌ Error sending SMS campaign:', error);
      message.error('Failed to send SMS campaign: ' + error.message);
    } finally {
      setSending(false);
    }
  };
  
  /**
   * Generate and show summary report
   */
  const showFilteringSummary = async () => {
    setLoading(true);
    try {
      const summary = await generateFilteringSummary(orgId);
      setFilteringSummary(summary);
      setSummaryModalVisible(true);
    } catch (error) {
      console.error('❌ Error generating summary:', error);
      message.error('Failed to generate summary');
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Table columns for staff preview
   */
  const staffColumns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => `${record.firstName} ${record.lastName}`,
      width: 150
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role) => <Tag color="blue">{role}</Tag>
    },
    {
      title: 'Site',
      dataIndex: 'siteName',
      key: 'siteName',
      width: 150,
      ellipsis: true
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, record) => `${record.state || ''}, ${record.country || ''}`,
      width: 120
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile',
      key: 'mobile',
      width: 130,
      render: (mobile) => (
        <Text code>
          <PhoneOutlined /> {mobile}
        </Text>
      )
    }
  ];
  
  if (initializing) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            <Text>Initializing enterprise bulk SMS system...</Text>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="bulk-sms-panel">
      {/* Header */}
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <Title level={4}>
            <SendOutlined /> Enterprise Bulk SMS System
            <Tag color="green" style={{ marginLeft: '8px' }}>
              Phase 2: Full Functionality
            </Tag>
          </Title>
          <Paragraph type="secondary">
            {crisisContext 
              ? `Send targeted SMS to staff during crisis response with enterprise-grade delivery tracking and analytics.`
              : `Complete SMS campaign management with templates, scheduling, and real-time analytics.`
            }
          </Paragraph>
        </div>

        {/* Recent Campaigns Quick Access */}
        {recentCampaigns.length > 0 && (
          <Alert
            message="Recent Campaigns"
            description={
              <Space wrap>
                {recentCampaigns.map(campaign => (
                  <Button
                    key={campaign.id}
                    size="small"
                    icon={<BarChartOutlined />}
                    onClick={() => {
                      setActiveCampaignId(campaign.id);
                      setAnalyticsVisible(true);
                    }}
                  >
                    {campaign.name}
                  </Button>
                ))}
              </Space>
            }
            type="info"
            style={{ marginBottom: '24px' }}
          />
        )}

        {/* Workflow Steps */}
        <Steps current={currentStep} style={{ marginBottom: '24px' }}>
          <Step title="Target Audience" description="Filter staff by location and role" />
          <Step title="Compose Message" description="Create or select SMS template" />
          <Step title="Review & Send" description="Confirm details and send" />
          <Step title="Monitor" description="Track delivery and analytics" />
        </Steps>
      </Card>

      {/* Step 0: Audience Targeting */}
      {currentStep === 0 && (
        <Card title="Step 1: Target Your Audience" style={{ marginTop: '16px' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Text strong>Country</Text>
              <Select
                style={{ width: '100%', marginTop: '4px' }}
                placeholder="Select country"
                value={selectedCountry}
                onChange={handleCountryChange}
                allowClear
                loading={loading}
              >
                {countries.map(country => (
                  <Option key={country} value={country}>{country}</Option>
                ))}
              </Select>
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <Text strong>State/Province</Text>
              <Select
                style={{ width: '100%', marginTop: '4px' }}
                placeholder="Select state"
                value={selectedState}
                onChange={handleStateChange}
                allowClear
                disabled={!selectedCountry}
                loading={loading}
              >
                {states.map(state => (
                  <Option key={state} value={state}>{state}</Option>
                ))}
              </Select>
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <Text strong>Site</Text>
              <Select
                style={{ width: '100%', marginTop: '4px' }}
                placeholder="Select site"
                value={selectedSite}
                onChange={setSelectedSite}
                allowClear
                disabled={!selectedState}
              >
                {sites.map(site => (
                  <Option key={site.id} value={site.name}>
                    {site.name}
                    {site.region && <Text type="secondary"> ({site.region})</Text>}
                  </Option>
                ))}
              </Select>
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <Text strong>Role</Text>
              <Select
                style={{ width: '100%', marginTop: '4px' }}
                placeholder="Select role"
                value={selectedRole}
                onChange={setSelectedRole}
                allowClear
              >
                {roles.map(role => (
                  <Option key={role} value={role}>{role}</Option>
                ))}
              </Select>
            </Col>
          </Row>
          
          <div style={{ marginTop: '24px' }}>
            <Space>
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={applyFilters}
                loading={loading}
              >
                Apply Filters
              </Button>
              
              <Button
                icon={<ReloadOutlined />}
                onClick={clearFilters}
              >
                Clear All
              </Button>
              
              {filteredResults && (
                <Button
                  icon={<EyeOutlined />}
                  onClick={() => setPreviewModalVisible(true)}
                >
                  Preview Recipients ({filteredResults.totalCount})
                </Button>
              )}
            </Space>
          </div>

          {/* Results Summary */}
          {filteredResults && (
            <div style={{ marginTop: '16px' }}>
              {filteredResults.totalCount > 0 ? (
                <Alert
                  message={`${filteredResults.totalCount} staff members selected`}
                  description={
                    <div>
                      <Space wrap style={{ marginTop: '8px' }}>
                        {Object.entries(filteredResults.filters).map(([key, value]) => (
                          value && (
                            <Tag key={key} color="blue">
                              {key}: {value}
                            </Tag>
                          )
                        ))}
                      </Space>
                      <div style={{ marginTop: '16px' }}>
                        <Button 
                          type="primary" 
                          onClick={nextStep}
                          disabled={!validateCurrentStep()}
                        >
                          Next: Compose Message
                        </Button>
                      </div>
                    </div>
                  }
                  type="success"
                  showIcon
                />
              ) : (
                <Alert
                  message="No staff members match the selected filters"
                  description="Try adjusting your filter criteria or removing some filters to get results."
                  type="warning"
                  showIcon
                />
              )}
            </div>
          )}
        </Card>
      )}

      {/* Step 1: Message Composition */}
      {currentStep === 1 && (
        <Card title="Step 2: Compose Your Message" style={{ marginTop: '16px' }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              {/* Template Selection */}
              <Card size="small" title="Message Templates" style={{ marginBottom: '16px' }}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Choose a template (optional)"
                  value={selectedTemplate?.id}
                  onChange={handleTemplateSelect}
                  allowClear
                >
                  {templates.map(template => (
                    <Option key={template.id} value={template.id}>
                      <Space>
                        <Tag color={
                          template.category === 'emergency' ? 'red' :
                          template.category === 'operational' ? 'orange' : 'blue'
                        }>
                          {template.category}
                        </Tag>
                        {template.name}
                      </Space>
                    </Option>
                  ))}
                </Select>
                
                {selectedTemplate && extractVariables(selectedTemplate.message).length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <Text strong>Template Variables:</Text>
                    <div style={{ marginTop: '8px' }}>
                      {extractVariables(selectedTemplate.message).map(variable => (
                        <div key={variable} style={{ marginBottom: '8px' }}>
                          <Text>{variable}:</Text>
                          <Input
                            size="small"
                            placeholder={`Enter ${variable}`}
                            value={templateVariables[variable] || ''}
                            onChange={(e) => handleVariableChange(variable, e.target.value)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>

              {/* Message Composition */}
              <Card size="small" title="Message Content">
                <TextArea
                  placeholder="Type your message here..."
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  rows={6}
                  maxLength={1600}
                  showCount
                />
                
                {costEstimate && (
                  <div style={{ marginTop: '16px' }}>
                    <Alert
                      message="Cost Estimate"
                      description={
                        <div>
                          <Text>
                            {costEstimate.segmentCount} SMS segment(s) × {filteredResults?.totalCount} recipients = {' '}
                            <Text strong>${costEstimate.totalCost.toFixed(2)} USD</Text>
                          </Text>
                        </div>
                      }
                      type="info"
                      showIcon
                    />
                  </div>
                )}
              </Card>
            </Col>

            <Col span={12}>
              {/* Message Preview */}
              <Card size="small" title="Message Preview">
                <div style={{
                  backgroundColor: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  minHeight: '120px'
                }}>
                  {messagePreview || 'Message preview will appear here...'}
                </div>
                
                <div style={{ marginTop: '16px' }}>
                  <Text type="secondary">
                    Characters: {messageText.length} | 
                    SMS segments: {Math.ceil(messageText.length / 160)} |
                    Recipients: {filteredResults?.totalCount || 0}
                  </Text>
                </div>
              </Card>

              {/* Priority and Scheduling */}
              <Card size="small" title="Message Options" style={{ marginTop: '16px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <Text strong>Priority Level:</Text>
                  <Radio.Group
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    style={{ display: 'block', marginTop: '8px' }}
                  >
                    <Radio value={PRIORITY_LEVELS.EMERGENCY}>
                      <Tag color="red">Emergency</Tag> - Immediate delivery
                    </Radio>
                    <Radio value={PRIORITY_LEVELS.HIGH}>
                      <Tag color="orange">High</Tag> - High priority queue
                    </Radio>
                    <Radio value={PRIORITY_LEVELS.NORMAL}>
                      <Tag color="blue">Normal</Tag> - Standard delivery
                    </Radio>
                  </Radio.Group>
                </div>

                <div>
                  <Switch
                    checked={isScheduled}
                    onChange={setIsScheduled}
                    style={{ marginRight: '8px' }}
                  />
                  <Text strong>Schedule for later</Text>
                  
                  {isScheduled && (
                    <div style={{ marginTop: '8px' }}>
                      <DatePicker
                        showTime
                        placeholder="Select date and time"
                        value={scheduledDate}
                        onChange={setScheduledDate}
                        disabledDate={(current) => current && current < moment().endOf('day')}
                      />
                    </div>
                  )}
                </div>
              </Card>
            </Col>
          </Row>

          <div style={{ marginTop: '24px' }}>
            <Space>
              <Button onClick={prevStep}>
                Previous: Target Audience
              </Button>
              <Button 
                type="primary" 
                onClick={nextStep}
                disabled={!validateCurrentStep()}
              >
                Next: Review & Send
              </Button>
            </Space>
          </div>
        </Card>
      )}

      {/* Step 2: Review and Send */}
      {currentStep === 2 && (
        <Card title="Step 3: Review & Send Campaign" style={{ marginTop: '16px' }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Card size="small" title="Campaign Summary">
                <div style={{ marginBottom: '16px' }}>
                  <Text strong>Recipients: </Text>
                  <Text>{filteredResults?.totalCount} staff members</Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Text strong>Estimated Cost: </Text>
                  <Text>${costEstimate?.totalCost.toFixed(2)} USD</Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Text strong>Priority: </Text>
                  <Tag color={
                    priority === PRIORITY_LEVELS.EMERGENCY ? 'red' :
                    priority === PRIORITY_LEVELS.HIGH ? 'orange' : 'blue'
                  }>
                    {priority === PRIORITY_LEVELS.EMERGENCY ? 'Emergency' :
                     priority === PRIORITY_LEVELS.HIGH ? 'High' : 'Normal'}
                  </Tag>
                </div>
                {isScheduled && scheduledDate && (
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong>Scheduled: </Text>
                    <Text>{scheduledDate.format('YYYY-MM-DD HH:mm')}</Text>
                  </div>
                )}
                
                <div style={{ marginTop: '24px' }}>
                  <Popconfirm
                    title="Send SMS Campaign"
                    description={`Are you sure you want to send this SMS to ${filteredResults?.totalCount} recipients?`}
                    onConfirm={sendSMSCampaign}
                    okText="Send SMS"
                    cancelText="Cancel"
                    okType="primary"
                  >
                    <Button
                      type="primary"
                      size="large"
                      icon={<SendOutlined />}
                      loading={sending}
                      disabled={!messageText.trim()}
                    >
                      {isScheduled ? 'Schedule SMS Campaign' : 'Send SMS Campaign'}
                    </Button>
                  </Popconfirm>
                </div>
              </Card>
            </Col>

            <Col span={12}>
              <Card size="small" title="Final Message Preview">
                <div style={{
                  backgroundColor: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  minHeight: '100px'
                }}>
                  {messagePreview}
                </div>
              </Card>
            </Col>
          </Row>

          <div style={{ marginTop: '24px' }}>
            <Space>
              <Button onClick={prevStep}>
                Previous: Compose Message
              </Button>
            </Space>
          </div>
        </Card>
      )}

      {/* Preview Modal */}
      <Modal
        title={`Staff Preview - ${filteredResults?.totalCount || 0} Recipients`}
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setPreviewModalVisible(false)}>
            Close
          </Button>
        ]}
      >
        {filteredResults && (
          <Table
            dataSource={filteredResults.staff}
            columns={staffColumns}
            rowKey="id"
            size="small"
            scroll={{ y: 400 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} staff members`
            }}
          />
        )}
      </Modal>

      {/* Analytics Dashboard */}
      <BulkSMSAnalyticsDashboard
        campaignId={activeCampaignId}
        visible={analyticsVisible}
        onClose={() => setAnalyticsVisible(false)}
      />
    </div>
  );
};

export default BulkSMSPanel; 